// CHECK SETS — shared rules for the builder, the readings forms and every
// screen that prints a reading (doc/check-sets-plan.md, Round A).
//
// NOTHING HERE IS SPECIALITY-SPECIFIC (owner, 26 Sep 2026). There is no field
// list, no unit list and no starter set: a doctor or the hospital admin
// defines every set and every field. This module only knows the six field
// TYPES and the server's limits, so a form can refuse what the server would
// refuse before a request is made. The server stays the authority — every
// rule below is re-checked there and its answer (with `field` / `index`) is
// shown where it points.
import { formatDay } from '../components/health/healthFormat';
import { tidyName } from './tidyName';
import { formatTime } from './clock';

export const FIELD_TYPES = ['number', 'text', 'select', 'multi', 'date', 'yesno'];

// Mirrors the contract's "Limits" table (validated server-side).
export const LIMITS = {
    name: 120,
    meantFor: 80,
    fieldsMin: 1,
    fieldsMax: 40,
    label: 80,
    unit: 20,
    optionsMin: 2,
    optionsMax: 50,
    option: 80,
    text: 1000,
    number: 10000000,
};

/** Trim and collapse inner whitespace, as the server does before comparing. */
export const collapse = (value) => String(value ?? '').replace(/\s+/g, ' ').trim();

/** `null`, a blank string and `[]` all mean "not filled" (dropped, not stored). */
export const isEmptyValue = (value) => value === null || value === undefined
    || (typeof value === 'string' && value.trim() === '')
    || (Array.isArray(value) && value.length === 0);

const hasOptions = (type) => type === 'select' || type === 'multi';

let uidCounter = 0;
const nextUid = () => {
    uidCounter += 1;
    return `new-${uidCounter}`;
};

/* ---------------------------------------------------------------------------
 * The builder's working copy of a set.
 * ------------------------------------------------------------------------- */

export const emptyField = () => ({
    uid: nextUid(),
    key: null,
    label: '',
    type: 'number',
    unit: '',
    options: [],
    required: false,
});

export const emptySetDraft = () => ({
    name: '',
    meantFor: '',
    staffMayRecord: false,
    requiredEveryVisit: false,
    fields: [emptyField()],
});

/** A set as the server sent it → the editor's working copy. */
export const setToDraft = (set) => ({
    name: set?.name || '',
    meantFor: set?.meantFor || '',
    staffMayRecord: set?.staffMayRecord === true,
    requiredEveryVisit: set?.requiredEveryVisit === true,
    fields: [...(set?.fields || [])]
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
        .map((f) => ({
            uid: f.key || nextUid(),
            key: f.key || null,
            label: f.label || '',
            type: FIELD_TYPES.includes(f.type) ? f.type : 'text',
            unit: f.unit || '',
            options: Array.isArray(f.options) ? [...f.options] : [],
            required: f.required === true,
        })),
});

/**
 * The working copy → the POST / PUT body. Existing fields send their `key`
 * back (so old readings keep lining up); new ones send none — the server
 * generates it. `position` is the array order, so it is not sent.
 */
export const draftToBody = (draft, ownerType) => {
    const body = {
        name: collapse(draft.name),
        staffMayRecord: draft.staffMayRecord === true,
        fields: draft.fields.map((f) => {
            const field = { label: collapse(f.label), type: f.type, required: f.required === true };
            if (f.key) field.key = f.key;
            if (f.type === 'number' && collapse(f.unit)) field.unit = collapse(f.unit);
            if (hasOptions(f.type)) field.options = f.options.map((o) => collapse(o)).filter(Boolean);
            return field;
        }),
    };
    if (ownerType === 'tenant') {
        body.meantFor = collapse(draft.meantFor) || null;
    } else {
        body.requiredEveryVisit = draft.requiredEveryVisit === true;
    }
    return body;
};

/**
 * What the server would refuse in a set definition, as i18n keys under
 * `checkSets.` (the component translates them):
 *   { name, meantFor, fields, byIndex: { [i]: { label, unit, options } } }
 * An empty object means "nothing to refuse".
 */
export const validateSetDraft = (draft, { ownerType = 'doctor' } = {}) => {
    const errors = {};
    const name = collapse(draft?.name);
    if (!name) errors.name = 'err_name_required';
    else if (name.length > LIMITS.name) errors.name = 'err_name_long';
    if (ownerType === 'tenant' && collapse(draft?.meantFor).length > LIMITS.meantFor) errors.meantFor = 'err_meant_for_long';

    const fields = draft?.fields || [];
    if (fields.length < LIMITS.fieldsMin) errors.fields = 'err_fields_required';
    else if (fields.length > LIMITS.fieldsMax) errors.fields = 'err_too_many_fields';

    const seen = new Map();
    const byIndex = {};
    fields.forEach((f, index) => {
        const e = {};
        const label = collapse(f.label);
        if (!label) e.label = 'err_label_required';
        else if (label.length > LIMITS.label) e.label = 'err_label_long';
        else {
            const norm = label.toLowerCase();
            if (seen.has(norm)) e.label = 'err_label_duplicate';
            else seen.set(norm, index);
        }
        if (f.type === 'number' && collapse(f.unit).length > LIMITS.unit) e.unit = 'err_unit_long';
        if (hasOptions(f.type)) {
            const options = (f.options || []).map((o) => collapse(o)).filter(Boolean);
            const distinct = new Set(options.map((o) => o.toLowerCase()));
            if (options.some((o) => o.length > LIMITS.option)) e.options = 'err_option_long';
            else if (distinct.size !== options.length) e.options = 'err_option_duplicate';
            else if (options.length < LIMITS.optionsMin || options.length > LIMITS.optionsMax) e.options = 'err_options_count';
        }
        if (Object.keys(e).length) byIndex[index] = e;
    });
    if (Object.keys(byIndex).length) errors.byIndex = byIndex;
    return errors;
};

export const hasSetErrors = (errors) => Boolean(errors && (errors.name || errors.meantFor || errors.fields
    || (errors.byIndex && Object.keys(errors.byIndex).length)));

/* ---------------------------------------------------------------------------
 * Readings.
 * ------------------------------------------------------------------------- */

/** The fields of a set in their display order. */
export const orderedFields = (set) => [...(set?.fields || [])]
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

/**
 * Stored values → what the form inputs hold. Numbers become strings (an input
 * holds text), everything else is kept as it is. Keys the set no longer has
 * are dropped — the server would refuse them (UNKNOWN_FIELD).
 */
export const formValuesFrom = (fields, values) => {
    const out = {};
    const src = values && typeof values === 'object' ? values : {};
    (fields || []).forEach((f) => {
        const v = src[f.key];
        if (v === undefined || v === null) return;
        if (f.type === 'number') out[f.key] = String(v);
        else if (f.type === 'multi') out[f.key] = Array.isArray(v) ? [...v] : [];
        else if (f.type === 'yesno') out[f.key] = v === true || v === false ? v : undefined;
        else out[f.key] = String(v);
    });
    return out;
};

/**
 * The form → the `{ fieldKey: value }` map the server takes (and the draft
 * stores). Empty fields are left out; a numeric string becomes a number; a
 * number still half-typed ("-") is kept as the text it is, so a draft never
 * loses a keystroke — validateReadingValues refuses it before a save.
 */
export const readingPayload = (fields, formValues) => {
    const out = {};
    (fields || []).forEach((f) => {
        const v = formValues?.[f.key];
        if (isEmptyValue(v)) return;
        if (f.type === 'number') {
            const text = String(v).trim().replace(',', '.');
            const n = Number(text);
            out[f.key] = text !== '' && Number.isFinite(n) ? n : String(v);
        } else if (f.type === 'multi') {
            out[f.key] = [...new Set(v)];
        } else if (f.type === 'yesno') {
            if (v === true || v === false) out[f.key] = v;
        } else {
            out[f.key] = typeof v === 'string' ? v.trim() : v;
        }
    });
    return out;
};

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const isRealDate = (value) => {
    const m = DATE_RE.exec(String(value || ''));
    if (!m) return false;
    const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
    const dt = new Date(Date.UTC(y, mo - 1, d));
    return dt.getUTCFullYear() === y && dt.getUTCMonth() === mo - 1 && dt.getUTCDate() === d;
};

/**
 * What the server would refuse in a reading, per field key, as i18n keys under
 * `checkSets.`. `_empty` means nothing at all is filled (EMPTY_READING).
 * Mandatory fields are checked against the set's CURRENT definition, as the
 * server does.
 */
export const validateReadingValues = (fields, formValues) => {
    const errors = {};
    const payload = readingPayload(fields, formValues);
    (fields || []).forEach((f) => {
        const v = payload[f.key];
        if (v === undefined) {
            if (f.required === true) errors[f.key] = 'err_required';
            return;
        }
        if (f.type === 'number') {
            if (typeof v !== 'number') errors[f.key] = 'err_number';
            else if (Math.abs(v) > LIMITS.number) errors[f.key] = 'err_number_range';
        } else if (f.type === 'text') {
            if (String(v).length > LIMITS.text) errors[f.key] = 'err_text_long';
        } else if (f.type === 'date') {
            if (!isRealDate(v)) errors[f.key] = 'err_date';
        } else if (f.type === 'select') {
            if (Array.isArray(f.options) && !f.options.includes(v)) errors[f.key] = 'err_option';
        } else if (f.type === 'multi') {
            if (Array.isArray(f.options) && v.some((o) => !f.options.includes(o))) errors[f.key] = 'err_option';
        }
    });
    if (Object.keys(payload).length === 0 && Object.keys(errors).length === 0) errors._empty = 'err_empty';
    return errors;
};

/** Two value maps are the same reading (key order does not matter). */
export const sameValues = (a, b) => {
    const norm = (m) => JSON.stringify(Object.keys(m || {}).sort().map((k) => [k, m[k]]));
    return norm(a) === norm(b);
};

/* ---------------------------------------------------------------------------
 * Printing a reading (contract §8).
 * ------------------------------------------------------------------------- */

/** One value as a person reads it: "80.5 kg", "Fever, Cough", "Yes". */
export const formatReadingValue = (item, t) => {
    const v = item?.value;
    if (isEmptyValue(v) && v !== false) return '';
    switch (item?.type) {
        case 'number':
            return item.unit ? `${v} ${item.unit}` : String(v);
        case 'multi':
            return Array.isArray(v) ? v.join(', ') : String(v);
        case 'date':
            return formatDay(v) || String(v);
        case 'yesno':
            return v === true ? t('checkSets.yes', 'Yes') : t('checkSets.no', 'No');
        default:
            if (Array.isArray(v)) return v.join(', ');
            if (typeof v === 'boolean') return v ? t('checkSets.yes', 'Yes') : t('checkSets.no', 'No');
            return item?.unit && typeof v === 'number' ? `${v} ${item.unit}` : String(v);
    }
};

/**
 * A shared-visit value carries no type ({ label, unit, value }): print it by
 * what the value is.
 */
export const formatLooseValue = (value, unit, t) => {
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'boolean') return value ? t('checkSets.yes', 'Yes') : t('checkSets.no', 'No');
    if (typeof value === 'number') return unit ? `${value} ${unit}` : String(value);
    if (typeof value === 'string' && DATE_RE.test(value)) return formatDay(value) || value;
    return value === null || value === undefined ? '' : String(value);
};

/** "Weight 80 kg, Symptoms Fever" — the one-line "Last: …" summary. */
export const summarizeItems = (items, t) => (items || [])
    .map((i) => `${i.label} ${formatReadingValue(i, t)}`.trim())
    .filter(Boolean)
    .join(', ');

/**
 * A reading's time of day in the viewer's clock, in the one clock format —
 * "9:52 AM" (utils/clock, owner 15 Oct 2026) — or '' for no usable time.
 * DISPLAY ONLY: no key, payload or comparison reads it. (The name is older
 * than the format; every caller just prints it.)
 */
export const hhmm = (iso) => formatTime(iso);

/**
 * Who took a reading, as the HEALTH RECORD says it (GET /health-record/…/readings):
 * the doctor by name, staff as "Recorded by staff" — the record names
 * clinicians, not employees, and the server sends no name for a staff reading.
 */
export const recorderLine = (r, t) => {
    if (r?.recordedRole === 'doctor') {
        return r.recordedByName ? t('checkSets.recorded_by_doctor', { name: tidyName(r.recordedByName) }) : t('checkSets.role_doctor');
    }
    if (r?.recordedRole === 'patient') return t('checkSets.recorded_by_patient');
    return t('checkSets.recorded_by_staff');
};

/** Offered sets of a visit, doctor's own first, then the hospital's. */
export const offeredSets = (data) => [...(data?.sets?.mine || []), ...(data?.sets?.hospital || [])];

/** Hospital sets grouped by their free "meant for" label (null = general). */
export const groupByMeantFor = (sets) => {
    const groups = new Map();
    (sets || []).forEach((s) => {
        const label = collapse(s.meantFor) || '';
        if (!groups.has(label)) groups.set(label, []);
        groups.get(label).push(s);
    });
    return [...groups.entries()]
        .sort(([a], [b]) => (a === '' ? 1 : b === '' ? -1 : a.localeCompare(b)))
        .map(([label, list]) => ({ label, sets: list }));
};

/** A visit status staff/admin may still record on (the server's own rule). */
export const isVisitOpenForStaff = (status) => !['Completed', 'NoShow', 'Cancelled'].includes(status);

/* ---------------------------------------------------------------------------
 * Trends (the readings endpoint's `series`).
 * ------------------------------------------------------------------------- */

export const SIX_MONTHS_MS = 183 * 24 * 60 * 60 * 1000;

/** The points of a series recorded in the last six months, oldest first. */
export const recentPoints = (series, now = Date.now()) => (series?.points || [])
    .map((p) => ({ ...p, t: new Date(p.recordedAt).getTime() }))
    .filter((p) => Number.isFinite(p.t) && typeof p.value === 'number' && p.t >= now - SIX_MONTHS_MS)
    .sort((a, b) => a.t - b.t);
